import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import { v4 as uuidv4 } from "uuid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";

import { useTranslation } from "react-i18next";

import { default as DataGrid } from "../../../components/LocalizedDataGrid";
import { GridToolbarContainer, GridActionsCellItem } from "@mui/x-data-grid";
import { Typography } from "@mui/material";
import { ApiContext } from "../../../App";

function AlertConfig(props) {
	const { value, onChange, errorMessages } = props;

	const { t } = useTranslation();

	const handleChange = (props) => {
		onChange(props.target.id, props.target.value);
	};

	const handleCategoryChange = (props) => {
		onChange("category", props.target.value);
	};

	const handlePriorityChange = (props) => {
		onChange("priority", props.target.value);
	};

	const handleTimeRangeEdit = (props) => {
		onChange(
			"time_ranges",
			value.time_ranges.map((item) =>
				item.id === props.id ? { ...item, [props.field]: props.value } : item
			)
		);
	};

	const handleDeleteTimeRange = (id) => {
		onChange(
			"time_ranges",
			value.time_ranges.filter((item) => item.id !== id)
		);
	};

	const handleAddTimeRange = () => {
		onChange("time_ranges", [
			...value.time_ranges,
			{
				id: uuidv4(),
				start: "09:00",
				end: "17:00",
				iso_week_day:
					value.time_ranges.length === 0
						? 1
						: Math.min(
								Math.max(
									...value.time_ranges.map((item) => item.iso_week_day)
								) + 1,
								7
							),
			},
		]);
	};

	const weekdays = [
		{ value: 1, label: t("Mon") },
		{ value: 2, label: t("Tue") },
		{ value: 3, label: t("Wed") },
		{ value: 4, label: t("Thu") },
		{ value: 5, label: t("Fri") },
		{ value: 6, label: t("Sat") },
		{ value: 7, label: t("Sun") },
	];

	const columns = [
		{
			field: "iso_week_day",
			headerName: t("Week day"),
			minWidth: 100,
			editable: true,
			type: "singleSelect",
			valueOptions: weekdays,
			valueFormatter: (props) =>
				weekdays.find((item) => item.value === props.value).label,
		},
		{
			field: "start",
			headerName: t("Start"),
			flex: 1,
			minWidth: 30,
			editable: true,
		},
		{
			field: "end",
			headerName: t("End"),
			flex: 1,
			minWidth: 30,
			editable: true,
		},
		{
			field: "actions",
			type: "actions",
			headerName: "",
			width: 10,
			cellClassName: "actions",
			getActions: ({ id }) => {
				return [
					<GridActionsCellItem
						icon={<DeleteIcon />}
						label={t("Delete")}
						onClick={() => handleDeleteTimeRange(id)}
						color="inherit"
					/>,
				];
			},
		},
	];

	const gridToolbar = (props) => {
		return (
			<GridToolbarContainer>
				<Button
					color="primary"
					size="small"
					startIcon={<AddIcon />}
					onClick={() => props.onClick()}
				>
					{t("Add time range")}
				</Button>
			</GridToolbarContainer>
		);
	};

	const getTimeRangeErrorMessages = (response) => {
		let messages = [];

		for (const [key, value] of Object.entries(response.time_ranges)) {
			for (const [key2, value2] of Object.entries(value)) {
				messages.push(...value2);
			}
		}
		return messages;
	};

	return (
		<Stack spacing={2}>
			<Typography
				variant="h6"
				color="primary"
			>
				{t("General") + ": "}
			</Typography>
			<Stack
				spacing={2}
				direction={"row"}
			>
				<TextField
					variant="standard"
					id="priority"
					label={t("Priority")}
					onChange={handlePriorityChange}
					value={value.priority}
					select
					helperText={
						errorMessages["priority"] ? errorMessages["priority"] : ""
					}
					error={errorMessages.hasOwnProperty("priority")}
				>
					<MenuItem
						key="LOW"
						value="LOW"
					>
						{t("Low")}
					</MenuItem>
					<MenuItem
						key="MEDIUM"
						value="MEDIUM"
					>
						{t("Medium")}
					</MenuItem>
					<MenuItem
						key="HIGH"
						value="HIGH"
					>
						{t("High")}
					</MenuItem>
				</TextField>
				<Box flex={1}>
					<TextField
						variant="standard"
						id="name"
						label={t("Name")}
						value={value.name ?? ""}
						onChange={handleChange}
						error={errorMessages.hasOwnProperty("name")}
						helperText={errorMessages["name"] ? errorMessages["name"] : ""}
						fullWidth
					/>
				</Box>
			</Stack>
			<TextField
				variant="standard"
				id="message"
				label={t("Message")}
				value={value.message ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("message")}
				helperText={errorMessages["message"] ? errorMessages["message"] : ""}
				multiline
			/>
			<Typography
				variant="h6"
				color="primary"
			>
				{t("Trigger") + ":"}
			</Typography>
			<Stack
				spacing={2}
				direction={"row"}
			>
				<TextField
					variant="standard"
					id="category"
					label={t("Condition")}
					onChange={handleCategoryChange}
					value={value.category}
					select
					helperText={
						errorMessages["category"] ? errorMessages["category"] : ""
					}
					error={errorMessages.hasOwnProperty("category")}
				>
					<MenuItem
						key="ACCEPTED_RANGE"
						value="ACCEPTED_RANGE"
					>
						{t("Outside range")}
					</MenuItem>
					<MenuItem
						key="EXACT_VALUE"
						value="EXACT_VALUE"
					>
						{t("Exact value")}
					</MenuItem>
				</TextField>
				{value.category === "ACCEPTED_RANGE" ? (
					<>
						<TextField
							variant="standard"
							id="min_value"
							label={t("Minimum")}
							value={value.min_value ?? ""}
							onChange={handleChange}
							error={errorMessages.hasOwnProperty("min_value")}
							helperText={
								errorMessages["min_value"] ? errorMessages["min_value"] : ""
							}
						/>
						<TextField
							variant="standard"
							id="max_value"
							label={t("Maximum")}
							value={value.max_value ?? ""}
							onChange={handleChange}
							error={errorMessages.hasOwnProperty("max_value")}
							helperText={
								errorMessages["max_value"] ? errorMessages["max_value"] : ""
							}
						/>
					</>
				) : (
					<TextField
						variant="standard"
						id="exact_value"
						label={t("Value")}
						value={value.exact_value ?? ""}
						onChange={handleChange}
						error={errorMessages.hasOwnProperty("exact_value")}
						helperText={
							errorMessages["exact_value"] ? errorMessages["exact_value"] : ""
						}
					/>
				)}
			</Stack>
			<Typography
				variant="h6"
				color="primary"
				gutterBottom
				flex={1}
			>
				{t("Effective on") + ":"}
			</Typography>
			{errorMessages.time_ranges
				? getTimeRangeErrorMessages(errorMessages).map((item) => {
						return <Typography color="red">{item}</Typography>;
					})
				: null}
			<Box
				height={315}
				minWidth={300}
			>
				<DataGrid
					rows={value.time_ranges}
					columns={columns}
					onCellEditCommit={handleTimeRangeEdit}
					hideFooter
					rowHeight={30}
					components={{
						Toolbar: gridToolbar,
					}}
					componentsProps={{
						toolbar: { onClick: handleAddTimeRange },
					}}
				/>
			</Box>
			<Stack>
				<Typography
					variant="h6"
					color="primary"
				>
					{t("Notifications") + ":"}
				</Typography>
				<TextField
					variant="standard"
					id="email"
					label={t("Email (optional)")}
					value={value.email ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("email")}
					helperText={errorMessages["email"] ? errorMessages["email"] : ""}
				/>
				<TextField
					variant="standard"
					id="phone_number"
					label={t("Phone number (optional)")}
					value={value.phone_number ?? ""}
					onChange={handleChange}
					error={errorMessages.hasOwnProperty("phone_number")}
					helperText={
						errorMessages["phone_number"] ? errorMessages["phone_number"] : ""
					}
				/>
			</Stack>
		</Stack>
	);
}

function PointSelector(props) {
	const { points, selectedPoints, controllers, onSelect, onUnselect } = props;

	const { t } = useTranslation();

	const selectedPointsSet = new Set(selectedPoints);

	const getControllerName = (params) => {
		const controller = controllers.filter(
			(item) => item.url === params.row.controller
		);
		if (controller.length > 0) return controller[0].name;
		return "";
	};

	const columns = [
		{
			field: "selectedPoint",
			headerName: t("Apply to?"),
			minWidth: 80,
			valueGetter: (params) => selectedPointsSet.has(params.row.url),
			renderCell: (params) => {
				return (
					<Checkbox
						checked={params.value}
						onChange={(event) => {
							if (!params.value) onSelect(params.id);
							else onUnselect(params.id);
						}}
					/>
				);
			},
		},
		{
			field: "controller_name",
			headerName: t("Controller"),
			flex: 1,
			minWidth: 80,
			valueGetter: getControllerName,
		},
		{ field: "identifier", headerName: "ID", flex: 1, minWidth: 200 },
		{
			field: "key",
			headerName: t("Key"),
			flex: 1,
			minWidth: 120,
			valueFormatter: (params) => {
				if (params.value == null) {
					return "";
				}
				const valueFormatted = t(params.value);
				return valueFormatted;
			},
		},
		{ field: "location", headerName: t("Location"), flex: 1, minWidth: 200 },
		{ field: "system", headerName: t("System"), flex: 1, minWidth: 180 },
		{ field: "name", headerName: t("Name"), flex: 1, minWidth: 180 },
	];

	return (
		<DataGrid
			rows={points}
			columns={columns}
			getRowId={(row) => row.url}
			disableSelectionOnClick={true}
		/>
	);
}

export default function AlertEditPopUp(props) {
	const {
		open,
		onCloseWithoutSaving,
		onCloseWithUpdate,
		points,
		controllers,
	} = props;
	const {alertsApi} = React.useContext(ApiContext)

	const { t } = useTranslation();

	const [activeTab, setActiveTab] = React.useState("alert");
	const [data, setData] = React.useState(props.data);
	const [errorMessages, setErrorMessages] = React.useState({});

	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

	const addIDs = (data) => {
		let data_copy = { ...data };
		data_copy.time_ranges = data_copy.time_ranges.map((item) => ({
			...item,
			id: uuidv4(),
		}));
		return data_copy;
	};

	React.useEffect(() => {
		setData(addIDs(props.data));
	}, [props.data]);

	const handleCloseWithoutSaving = () => {
		setData(addIDs(props.data));
		onCloseWithoutSaving();
		setErrorMessages({});
	};

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	const handleFormChange = (id, value) => {
		setData((old) => ({ ...old, [id]: value }));
		setErrorMessages((old) =>
			Object.fromEntries(Object.entries(old).filter(([key]) => key !== id))
		);
	};

	const handleSave = () => {
		setErrorMessages({});

		const timeRangesWithoutID = data.time_ranges.map((item) => ({
			start: item.start,
			end: item.end,
			iso_week_day: item.iso_week_day,
		}));

		let newData = {
			...data,
			time_ranges: timeRangesWithoutID,
		};

		if (data.category === "EXACT_VALUE") {
			if (newData.hasOwnProperty("min_value")) delete newData.min_value;
			if (newData.hasOwnProperty("max_value")) delete newData.max_value;
		} else {
			if (newData.hasOwnProperty("exact_value")) delete newData.exact_value;
		}

		if (data.email === "") newData.email = null;
		if (data.phone_number === "") newData.phone_number = null;

		const onSuccess = (data) => {
			const updatedRow = { ...newData, id: data.url };
			onCloseWithUpdate(updatedRow);
		};

		const onError = (response) => {
			setErrorMessages(response);

			const non_field_errors = Object.entries(response)
				.filter(([key]) => key === "non_field_errors")
				.map(([key, value]) => value);
			if (non_field_errors.length > 0) {
				alert(non_field_errors.join("; "));
			}
			setActiveTab("alert");
		};

		if (!data.hasOwnProperty("id")) {
			let payload = { ...newData };

			alertsApi.create(payload, onSuccess, onError);
		} else {
			let payload = { ...newData };
			delete payload.id;

			alertsApi.update(newData.id, payload, onSuccess, onError);
		}
	};

	const handleSelectPoint = (url) => {
		setData((d) => ({ ...d, points: [...d.points, url] }));
	};

	const handleUnselectPoint = (url) => {
		setData((d) => ({ ...d, points: d.points.filter((item) => item !== url) }));
	};

	return (
		<Dialog
			open={open}
			onClose={handleCloseWithoutSaving}
			fullScreen={fullScreen}
			fullWidth={activeTab === "inputs"}
			maxWidth={"lg"}
		>
			<DialogTitle>Edit alert</DialogTitle>
			<DialogContent>
				<Box sx={{ borderBottom: 1, marginBottom: 3, borderColor: "divider" }}>
					<Tabs
						value={activeTab}
						onChange={handleTabChange}
					>
						<Tab
							label={t("Alert")}
							value="alert"
						/>
						<Tab
							label={t("Points")}
							value="points"
						/>
					</Tabs>
				</Box>
				{activeTab === "alert" ? (
					<AlertConfig
						value={data}
						onChange={handleFormChange}
						errorMessages={errorMessages}
					/>
				) : null}
				<Box
					sx={{
						display: activeTab === "points" ? "block" : "none",
						height: "calc(100vh - 300px)",
						maxHeight: 1500,
						width: "calc(100vh)"
					}}
				>
					<PointSelector
						points={points}
						selectedPoints={data.points}
						controllers={controllers}
						onSelect={handleSelectPoint}
						onUnselect={handleUnselectPoint}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={handleCloseWithoutSaving}
					autoFocus
				>
					{t("Cancel")}
				</Button>
				<Button onClick={handleSave}>{t("Save")}</Button>
			</DialogActions>
		</Dialog>
	);
}
